import React, { useCallback, useEffect, useState } from 'react';
import { Drawer, Input, Button, Typography, Spin, Empty, Popconfirm, Space } from 'antd';
import { SendOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import moment from 'moment';
import API from '../../../API';
import { notifyError } from '../../../Util';
import './ReportComments.css';

const { Text } = Typography;
const { TextArea } = Input;

const ReportComments = ({ visible, onClose, reportId }) => {
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [body, setBody] = useState('');
    const [posting, setPosting] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editBody, setEditBody] = useState('');

    const load = useCallback(async () => {
        if (!reportId) {
            return;
        }

        try {
            setLoading(true);
            const response = await API.getReportComments(reportId);
            setComments(response.data || []);
        } catch (error) {
            notifyError('Failed to load comments');
        } finally {
            setLoading(false);
        }
    }, [reportId]);

    useEffect(() => {
        if (visible) {
            setBody('');
            setEditingId(null);
            load();
        }
    }, [visible, load]);

    const handlePost = async () => {
        if (!body.trim()) {
            return;
        }

        try {
            setPosting(true);
            await API.createReportComment(reportId, body.trim());
            setBody('');
            await load();
        } catch (error) {
            notifyError(error?.response?.data?.message || 'Failed to post comment');
        } finally {
            setPosting(false);
        }
    };

    const handleSaveEdit = async (id) => {
        if (!editBody.trim()) {
            return;
        }

        try {
            await API.updateReportComment(id, editBody.trim());
            setEditingId(null);
            await load();
        } catch (error) {
            notifyError(error?.response?.data?.message || 'Failed to update comment');
            await load();
        }
    };

    const handleDelete = async (id) => {
        try {
            await API.deleteReportComment(id);
            await load();
        } catch (error) {
            notifyError(error?.response?.data?.message || 'Failed to delete comment');
            await load();
        }
    };

    const renderComment = (comment) => {
        if (comment.is_deleted) {
            return (
                <div key={comment.id} style={{ marginBottom: 14 }}>
                    <Text type="secondary" italic>comment removed</Text>
                </div>
            );
        }

        if (editingId === comment.id) {
            return (
                <div key={comment.id} style={{ marginBottom: 14 }}>
                    <TextArea
                        rows={3}
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                    />
                    <Space style={{ marginTop: 6 }}>
                        <Button type="primary" size="small" onClick={() => handleSaveEdit(comment.id)}>
                            Save
                        </Button>
                        <Button size="small" onClick={() => setEditingId(null)}>Cancel</Button>
                    </Space>
                </div>
            );
        }

        return (
            <div key={comment.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 14 }}>
                <div style={{ minWidth: 0 }}>
                    <Text strong>{comment.author?.name || 'Unknown'}</Text>
                    <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                        {moment(comment.created_at).fromNow()}
                        {comment.is_edited ? ' (edited)' : ''}
                    </Text>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{comment.body}</div>
                </div>
                {(comment.can_edit || comment.can_delete) && (
                    <Space size={0}>
                        {comment.can_edit && (
                            <Button
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                title="Edit"
                                aria-label="Edit"
                                onClick={() => {
                                    setEditingId(comment.id);
                                    setEditBody(comment.body);
                                }}
                            />
                        )}
                        {comment.can_delete && (
                            <Popconfirm title="Delete this comment?" onConfirm={() => handleDelete(comment.id)}>
                                <Button type="text" size="small" icon={<DeleteOutlined />} title="Delete" aria-label="Delete" />
                            </Popconfirm>
                        )}
                    </Space>
                )}
            </div>
        );
    };

    return (
        <Drawer
            title="Comments"
            placement="right"
            width={460}
            open={visible}
            onClose={onClose}
            bodyStyle={{ display: 'flex', flexDirection: 'column', padding: 0 }}
        >
            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                {loading ? (
                    <Spin style={{ display: 'block', margin: '32px auto' }} />
                ) : comments.length ? (
                    comments.map(renderComment)
                ) : (
                    <Empty description="No comments yet" />
                )}
            </div>

            <div style={{ borderTop: '1px solid #f0f0f0', padding: 12 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                    <TextArea
                        rows={2}
                        value={body}
                        placeholder="Add a comment"
                        onChange={(e) => setBody(e.target.value)}
                        onPressEnter={(e) => {
                            if (!e.shiftKey) {
                                e.preventDefault();
                                handlePost();
                            }
                        }}
                    />
                    <Button
                        type="primary"
                        className="comment-send-button"
                        icon={<SendOutlined />}
                        loading={posting}
                        disabled={!body.trim()}
                        onClick={handlePost}
                        title="Send"
                        aria-label="Send"
                    />
                </div>
            </div>
        </Drawer>
    );
};

export default ReportComments;
